import { useEffect, useMemo, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Link } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import './Quiz.css';
import * as Helper from '../service/Helper.js';

import SingleAudio from '../pages/SingleAudio';

export default function Quiz() {

    const [searchParams] = useSearchParams()
    const lernfeld = searchParams.get('lernfeld');
    const quizType = searchParams.get('type');
    const quizLevel = searchParams.get('level');

    const navigate = useNavigate()
    const [data, setData] = useState(null)
    const [idx, setIdx] = useState(0)
    const [selected, setSelected] = useState([])
    const [submitted, setSubmitted] = useState(false)
    const [isCorrect, setIsCorrect] = useState(null)
    const [answers, setAnswers] = useState({})
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [correctAnswers, setCorrectAnswers] = useState([])

    // Merkt sich das aktuell spielende Audio-Element
    const currentAudioRef = useRef(null);

    const handlePlayRequest = (audioEl) => {
        if (currentAudioRef.current && currentAudioRef.current !== audioEl) {
            const oldAudio = currentAudioRef.current;

            // altes Audio stoppen & zurücksetzen
            oldAudio.pause();
            oldAudio.currentTime = 0;
            oldAudio.load();

            // Icon dieses alten Audios aktualisieren
            const container = oldAudio.closest('.audio-button');
            const icon = container?.querySelector('.audio-icon');
            if (icon) {
                icon.src = '/img/play-icon.svg';
                icon.alt = 'Play';
            }
        }

        currentAudioRef.current = audioEl;
    };


    const handlePause = (audioEl) => {
        // Wenn das pausierte Audio das aktuelle ist → Referenz löschen
        if (currentAudioRef.current === audioEl) {
            currentAudioRef.current.currentTime = 0; // wieder an den Anfang
            currentAudioRef.current = null;
        }
    };

    useEffect(() => {
        (async () => {
            try {
                setLoading(true)

                const questionFileName = Helper.getQuestionFileName(lernfeld, quizType, quizLevel);

                const res = await fetch(`/questions/${questionFileName}`)

                if (!res.ok) throw new Error(`HTTP ${res.status}`)
                const json = await res.json()

                // Wenn audio kein Array ist, dann machen wir hieraus ein Array
                for (const q of json.questions) {
                    if (q.audio && !Array.isArray(q.audio)) {
                        q.audio = [q.audio]
                    }
                }
                // ------------------------------------------------------------

                if (json.zufaelligeReihenfolge) {
                    json.questions = Helper.shuffle(json.questions)
                }

                setData(json)
            } catch (e) {
                setError(e?.message ?? 'Unbekannter Fehler beim Laden der Fragen.')
            } finally {
                setLoading(false)
            }
        })()
    }, [])

    const question = useMemo(() => {
        if (!data) return null
        return data.questions[idx] ?? null
    }, [data, idx])

    useEffect(() => {
        // Reset selection when question changes
        if (question) {
            setSelected(answers[question.id] ?? [])
        }
        setSubmitted(false)
        setIsCorrect(null)
    }, [question?.id])

    const toggle = (i) => {
        if (submitted) return
        if (!question) return

        if (question.multipleCorrect) {
            setSelected(prev =>
                prev.includes(i)
                    ? prev.filter(x => x !== i)
                    : [...prev, i]
            )
        } else {
            setSelected([i])
        }
    }

    const evaluate = (q, picks) => {
        const a = [...picks].sort((x, y) => x - y)
        console.log(a);
        const b = [...q.correct].sort((x, y) => x - y)
        console.log(b);
        if (a.length !== b.length) return false
        return a.every((v, i) => v === b[i])
    }

    const onSubmit = () => {
        if (!question) return
        const ok = evaluate(question, selected)
        if (ok === false) {
            const correctAnswers = [...question.correct].sort((x, y) => x - y)
            setCorrectAnswers(correctAnswers);
        }
        setIsCorrect(ok)
        setSubmitted(true)
        setAnswers(prev => ({ ...prev, [question.id]: selected }))
    }

    const onNext = () => {
        setCorrectAnswers([]);
        if (!data) return
        if (idx < data.questions.length - 1) {
            setIdx(i => i + 1)
        } else {
            // compute summary result and navigate
            const score = computeScore(data.questions, { ...answers, [question.id]: selected })
            navigate('/summary', { state: { score, total: data.questions.length } })
        }
    }

    const onAbort = () => {
        navigate('/')
    }

    // In lernfeld steht z.B. Notation1, Formen1, Gestaltungsprinzipien2, Instrumente3
    // Die Ziffer am Ende muss entfernt werden, dann muss dem "lernfeld" hinzugefügt werden, dann ans End wieder die vorher entfernte Ziffer hinzufügen.

    const backTo = "/" + lernfeld.toLowerCase().replace(/^([A-Za-z]+)(\d)$/, "$1lernfeld$2");

    if (loading) return <p>Lade Fragen…</p>
    if (error) return <div className="card error">Fehler: {error}</div>
    if (!data || !question) return <p>Keine Daten gefunden.</p>

    return (

        <div
            className="bg-notation-wrapper view"
            style={{
                backgroundImage: `url(${process.env.PUBLIC_URL + '/img/bg-yellow.svg'})`,
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'bottom left',
                minHeight: '100vh',
                width: '100%',
            }}
        >
            <main className="container" role="main">

                <Link to={backTo} className="back-btn">← Zurück</Link>

                <section className="mainquiz-card">
                    <div className="mainquiz-header">
                        <div className="meta">Frage {idx + 1} / {data.questions.length}</div>
                        <h2>{data.title}</h2>
                    </div>

                    <p className="prompt">{question.prompt}</p>

                    {question.image && (
                        <figure>
                            <img src={question.image.src} height={160} />
                        </figure>
                    )}

                    {question.audio && (
                        <div className="audio-container">
                            {question.audio.map((a, i) => (
                                <SingleAudio
                                    key={a.id}
                                    src={a.src}
                                    label={a.label}
                                    onPlayRequest={handlePlayRequest}
                                    onPauseGlobal={handlePause}
                                />
                            ))}
                        </div>
                    )}

                    <ul className="choices">
                        {question.choices.map((c, i) => (
                            <li key={i} className={selected.includes(i) ? 'choice selected' : 'choice'}>
                                <label>
                                    <input
                                        type={question.multipleCorrect ? 'checkbox' : 'radio'}
                                        name={question.id}
                                        checked={selected.includes(i)}
                                        onChange={() => toggle(i)}
                                    />
                                    <span className="choice-content" >
                                        
                                        {c.kind === 'text' && <span>{c.text}</span>}
                                        {c.kind === 'image' && (
                                            <figure>
                                                <img src={c.src} alt={c.label ?? 'Bild'} height={160} />
                                                {c.label && <figcaption>{c.label}</figcaption>}
                                            </figure>
                                        )}
                                        {c.kind === 'audio' && (
                                            <div className="audio-container">
                                                <SingleAudio
                                                    key={c.id}
                                                    src={c.src}
                                                    label={c.label}
                                                    onPlayRequest={handlePlayRequest}
                                                    onPauseGlobal={handlePause}
                                                />
                                            </div>
                                        )}
                                        {submitted && correctAnswers.includes(i) && (
                                            <div className="correct-indicator">✅</div>
                                        )}
                                        
                                    </span>
                                </label>
                            </li>
                        ))}
                    </ul>

                    {!submitted ? (
                        <div className="actions">
                            <button
                                className="btn"
                                disabled={selected.length === 0}
                                onClick={onSubmit}
                            >
                                Antwort prüfen
                            </button>
                            <button className="btn ghost" onClick={onAbort}>Abbrechen</button>
                        </div>
                    ) : (
                        <div className="actions">
                            <div className={isCorrect ? 'feedback ok' : 'feedback bad'}>
                                {isCorrect ? '✅ Korrekt!' : '❌ Leider falsch.'}
                            </div>
                            <button
                                className="btn"
                                onClick={onNext}
                            >
                                {idx === data.questions.length - 1 ? 'Zur Auswertung' : 'Nächste Frage'}
                            </button>
                            <button className="btn ghost" onClick={onAbort}>Abbrechen</button>
                        </div>
                    )}
                </section>

            </main>
        </div>
    )
}

function computeScore(questions, answers) {
    let correct = 0
    for (const q of questions) {
        const picks = answers[q.id] ?? []
        const a = [...picks].sort((x, y) => x - y)
        const b = [...q.correct].sort((x, y) => x - y)
        if (a.length === b.length && a.every((v, i) => v === b[i])) correct++
    }
    return { correct }
}
